// Soft chimes for offers and raises (WebAudio, no files). One mute toggle for the whole console,
// remembered per browser. Browsers only let audio start after the page has had a click or key press;
// before that a chime is silently skipped.

import { useSyncExternalStore } from 'react';

const KEY = 'eyeqsoc.muted';
let muted = (() => {
  try {
    return localStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
})();
const subs = new Set<() => void>();

export function isMuted(): boolean {
  return muted;
}

export function setMuted(v: boolean): void {
  muted = v;
  try {
    localStorage.setItem(KEY, v ? '1' : '0');
  } catch {
    /* storage blocked: keep it for this page */
  }
  for (const cb of subs) cb();
}

export function useMuted(): boolean {
  return useSyncExternalStore(
    (cb) => {
      subs.add(cb);
      return () => subs.delete(cb);
    },
    () => muted,
    () => false,
  );
}

let ctx: AudioContext | null = null;

function audio(): AudioContext | null {
  if (typeof window === 'undefined' || typeof window.AudioContext === 'undefined') return null;
  if (!ctx) {
    try {
      ctx = new window.AudioContext();
    } catch {
      return null;
    }
  }
  if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
  return ctx;
}

/** Notes (Hz) and spacing for each chime. Offer: rising two-tone. Raise: three quick notes. */
const CHIMES = {
  offer: { notes: [659.25, 880], gap: 0.16, len: 0.42 },
  raise: { notes: [880, 698.46, 880], gap: 0.12, len: 0.3 },
  /** Phase 4: the agent asks for an OK (soft falling pair). */
  ask: { notes: [987.77, 783.99], gap: 0.14, len: 0.34 },
} as const;

export type ChimeKind = keyof typeof CHIMES;

export function chime(kind: ChimeKind): void {
  if (muted) return;
  const ac = audio();
  if (!ac || ac.state !== 'running') return;
  const c = CHIMES[kind];
  const t0 = ac.currentTime + 0.02;
  c.notes.forEach((f, i) => {
    const osc = ac.createOscillator();
    const g = ac.createGain();
    osc.type = 'sine';
    osc.frequency.value = f;
    const start = t0 + i * c.gap;
    g.gain.setValueAtTime(0.0001, start);
    g.gain.exponentialRampToValueAtTime(0.09, start + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, start + c.len);
    osc.connect(g).connect(ac.destination);
    osc.start(start);
    osc.stop(start + c.len + 0.05);
  });
}

// Unlock audio on the first user gesture so the first offer can chime.
if (typeof window !== 'undefined') {
  const unlock = () => {
    audio();
    window.removeEventListener('pointerdown', unlock);
    window.removeEventListener('keydown', unlock);
  };
  window.addEventListener('pointerdown', unlock);
  window.addEventListener('keydown', unlock);
}
